import { isEmpty } from 'lodash';

import type { AiAction, AiChatReference, Insight } from '~/components/AiChat/types';
import { useActionsData } from '~/components/ClaimPage/CliveApp/CliveInsights/contexts/ActionsDataProvider';
import { useInsightsData } from '~/components/ClaimPage/CliveApp/CliveInsights/contexts/InsightsDataProvider';
import { useCms } from '~/components/hooks/useCms';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

export interface CliveInsightsViewModel {
  // Insights
  insights: Insight[];
  references: Record<string, AiChatReference>;
  isInsightsEnabled: boolean;
  isInsightsLoading: boolean;
  hasInsights: boolean;
  // Actions
  filteredActions: AiAction[];
  isActionsLoading: boolean;
  hasActions: boolean;
  reloadActions: () => Promise<void>;
  areActionsInConductorHub: boolean;
  // Combined
  isLoading: boolean;
  hasNoData: boolean;
}

/**
 * Presentation-agnostic view model for the Clive Insights tab.
 *
 * Aggregates the insights/actions data contexts and the insights feature flag into a single
 * object so that the views can render purely from this hook without re-reading contexts or
 * feature flags directly.
 *
 * The Clive Insights tab as a whole - actions included - is gated by the
 * `clive_insights_claim_tab` Clive configuration toggle in the sidebar tab definition
 * (`useClaimSideBarTabs/utils.ts`), which is what decides whether this tab mounts at all, so
 * nothing below re-checks it.
 */
export const useCliveInsightsViewModel = (): CliveInsightsViewModel => {
  const { userOrganization } = useCms();

  const isInsightsEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.AI_SERVICE_UI_INSIGHTS_TAB_DISPLAY
  );

  const { insights, references, isLoadingFirstTime: isInsightsLoading } = useInsightsData();
  const {
    filteredActions,
    isLoadingFirstTime: isActionsLoading,
    reloadData: reloadActions,
    isDisabled: areActionsInConductorHub,
  } = useActionsData();

  const hasInsights = isInsightsEnabled && !isEmpty(insights);
  const hasActions = !isEmpty(filteredActions);

  return {
    insights,
    references,
    isInsightsEnabled,
    isInsightsLoading,
    hasInsights,
    filteredActions,
    isActionsLoading,
    hasActions,
    reloadActions,
    areActionsInConductorHub,
    isLoading: (isInsightsEnabled && isInsightsLoading) || isActionsLoading,
    hasNoData: !hasInsights && !hasActions,
  };
};
